import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemElementProps } from "./contracts/dom";
import { Text } from "./Text";

const FIELD_FEEDBACK_WIDTH_CLASSES = {
  full: "w-full max-w-full",
  fit: "w-fit max-w-full",
  wide: "w-search-wide max-w-full",
} as const;

const FIELD_FEEDBACK_MAX_HEIGHT_CLASSES = {
  editor: "max-h-field-editor-max",
} as const;

export interface FieldFeedbackProps extends Omit<DesignSystemElementProps<"div">, "children"> {
  children: ReactNode;
  error?: ReactNode;
  errorId?: string;
  supportingText?: ReactNode;
  invalid?: boolean;
  highlightInvalid?: boolean;
  width?: keyof typeof FIELD_FEEDBACK_WIDTH_CLASSES;
  maxHeight?: keyof typeof FIELD_FEEDBACK_MAX_HEIGHT_CLASSES;
  layer?: "raised";
  pushToEnd?: boolean;
}

/** Presents a control with its validation message and optional trailing help text. */
export function FieldFeedback({
  children,
  error,
  errorId,
  supportingText,
  invalid,
  highlightInvalid,
  width = "full",
  maxHeight,
  layer,
  pushToEnd,
  ...nativeProps
}: FieldFeedbackProps): ReactNode {
  return (
    <div
      {...nativeProps}
      className={twJoin(
        "relative flex flex-col gap-xs",
        FIELD_FEEDBACK_WIDTH_CLASSES[width],
        maxHeight && FIELD_FEEDBACK_MAX_HEIGHT_CLASSES[maxHeight],
        layer === "raised" && "z-raised",
        pushToEnd && "mt-auto",
      )}
    >
      {highlightInvalid && invalid && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-space-xs rounded-lg border border-border-danger"
        />
      )}
      {children}
      {(error != null || supportingText != null) && (
        <div className="flex w-full justify-between" data-testid="form-input-meta">
          {error != null && (
            <Text
              id={errorId}
              variant="overline"
              tone="danger"
              overflow="wrap"
              data-testid="form-input-error"
              role="alert"
            >
              {error}
            </Text>
          )}
          {supportingText != null && (
            <span
              className={twJoin(
                "ml-auto text-overline whitespace-nowrap",
                invalid ? "text-text-danger" : "text-text-secondary",
              )}
            >
              {supportingText}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function FieldCharacterCount({ current, maximum }: { current: number; maximum: number }): ReactNode {
  return (
    <>
      <span className={current > maximum ? "text-text-danger" : undefined}>{current}</span>/{maximum}
    </>
  );
}
